import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Fade,
  Zoom,
  Button
} from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { getFournisseurs } from '../redux/actions/fournisseurActions';
import CliniqueService from '../services/CliniqueService';
import DosageService from '../services/DosageService';
import FournisseurService from '../services/FournisseurService';
import { Autocomplete } from "@material-ui/lab";
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';

// Icônes Material-UI
import AddIcon from '@material-ui/icons/Add';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import CategoryIcon from '@material-ui/icons/Category';
import BusinessIcon from '@material-ui/icons/Business';
import BarcodeIcon from '@material-ui/icons/FeaturedPlayList';
import { Edit, Save } from '@material-ui/icons';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import produitService from '../services/ProduitService';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(1),
  },
  gridItem: {
    padding: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  editButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(242, 101, 34, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #f57c32 100%)',
      boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  injectableSection: {
    backgroundColor: '#f0fdf9',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '2px solid #ccfbf1',
    marginBottom: theme.spacing(2),
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  calculatedField: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f8fafc !important',
      '& .MuiInputBase-input': {
        fontWeight: 600,
        color: '#226D68',
      }
    }
  },
  disabledField: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5 !important',
      '& .MuiInputBase-input': {
        color: '#666666 !important',
        cursor: 'not-allowed',
        WebkitTextFillColor: '#666666 !important',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0 !important',
      }
    },
    '& .MuiInputLabel-root': {
      color: '#666666 !important',
    },
    '& .MuiFormHelperText-root': {
      color: '#666666 !important',
    }
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  formTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  headerIcon: {
    fontSize: 40,
    color: '#226D68',
    marginBottom: 8,
  },
  editHeaderIcon: {
    fontSize: 40,
    color: '#f26522',
    marginBottom: 8,
  }
}));

// Validation manuelle pour remplacer Yup
const validateForm = (values, isEditMode) => {
  const errors = {};

  if (!values.nomProd || values.nomProd.trim().length < 3) {
    errors.nomProd = "Nom du produit requis (min. 3 caractères)";
  }

  if (!values.prixProd || isNaN(values.prixProd) || Number(values.prixProd) <= 0) {
    errors.prixProd = "Prix de vente requis et doit être positif";
  }

  if (!values.intituleFam) {
    errors.intituleFam = "Famille requise";
  }

  if (!values.fournisseur) {
    errors.fournisseur = "Fournisseur requis";
  }

  if (!values.dateExpiration) {
    errors.dateExpiration = "Date d'expiration requise";
  }

  if (!values.formepharmaceutique) {
    errors.formepharmaceutique = "Forme pharmaceutique requise";
  }

  // Validation de la quantité
  const quantiteValue = Number(values.quantite) || 0;
  if (quantiteValue < 0) {
    errors.quantite = "La quantité ne peut pas être négative";
  }

  // Validation des nombres
  if (values.tva && (isNaN(values.tva) || Number(values.tva) < 0)) {
    errors.tva = "TVA doit être un nombre positif";
  }

  if (values.boites && (isNaN(values.boites) || Number(values.boites) < 0)) {
    errors.boites = "Nombre de boites doit être positif";
  }

  if (values.prixBoite && (isNaN(values.prixBoite) || Number(values.prixBoite) < 0)) {
    errors.prixBoite = "Prix de la boite doit être positif";
  }

  if (values.pieces && (isNaN(values.pieces) || Number(values.pieces) < 0)) {
    errors.pieces = "Pièces par boite doit être positif";
  }

  // Validation pour prix_unitaire (non obligatoire mais doit être positif si renseigné)
  if (values.prix_unitaire && (isNaN(values.prix_unitaire) || Number(values.prix_unitaire) < 0)) {
    errors.prix_unitaire = "Prix unitaire doit être positif";
  }

  return errors;
};

// Composant DateTimePicker simplifié si le vôtre ne fonctionne pas
const SimpleDatePicker = ({ value, onChange, error, helperText, ...props }) => {
  return (
    <TextField
      {...props}
      type="date"
      value={value || ''}
      onChange={onChange}
      error={error}
      helperText={helperText}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{
        shrink: true,
      }}
    />
  );
};

const RegistrationForm = ({ onClose, refresh, initialData, isEditMode = false, productId = null }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [formes, setFormes] = useState([]);
  const [fournisseurs, setFournisseur] = useState([]);
  const [familles, setFamille] = useState([]);
  const [typeInjectable, setTypeInjectable] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    dispatch(getFournisseurs());
    const fetchData = async () => {
      try {
        const [fournisseursResponse, formesResponse, famillesResponse] = await Promise.all([
          FournisseurService.getFournisseurs(),
          DosageService.getFormes(),
          CliniqueService.getFamilles()
        ]);

        setFournisseur(fournisseursResponse.data || []);
        setFormes(formesResponse.data || []);
        setFamille(famillesResponse.data || []);
      } catch (error) {
        console.error('Erreur chargement données:', error);
        toast.error("Erreur lors du chargement des données");
      }
    };

    fetchData();
  }, [dispatch]);

  // Déterminer la forme pharmaceutique initiale pour le typeInjectable
  useEffect(() => {
    if (initialData && initialData.formepharmaceutique) {
      setTypeInjectable(initialData.formepharmaceutique);
    }
  }, [initialData]);

  // Fonction pour nettoyer les données avant la soumission
  const cleanFormData = (values) => {
    const cleaned = {
      nomProd: values.nomProd || '',
      prixProd: values.prixProd || '',
      intituleFam: values.intituleFam || '',
      fournisseur: values.fournisseur || '',
      dateExpiration: values.dateExpiration || '',
      quantite: values.quantite || 0,
      codeBarre: values.codeBarre || '',
      formepharmaceutique: values.formepharmaceutique || '',
      tva: values.tva || 0,
      boites: values.boites || 0,
      prixBoite: values.prixBoite || 0,
      pieces: values.pieces || 0,
      prix_unitaire: values.prix_unitaire || '', // Nouveau champ
    };
    
    return cleaned;
  };

  // Utiliser les données initiales si en mode édition
  const initialValues = initialData ? cleanFormData(initialData) : {
    nomProd: '',
    prixProd: '',
    intituleFam: '',
    fournisseur: '',
    dateExpiration: '',
    quantite: 0,
    codeBarre: '',
    formepharmaceutique: '',
    tva: 0,
    boites: 0,
    prixBoite: 0,
    pieces: 0,
    prix_unitaire: ''
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      // Validation manuelle avant soumission
      const errors = validateForm(values, isEditMode);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setSubmitting(false);
        return;
      }

      const today = new Date();
      
      // Calculer la quantité pour les injectables
      const quantiteFinale = typeInjectable === 'Injectables' 
        ? (Number(values.boites) || 0) * (Number(values.pieces) || 0)
        : Number(values.quantite) || 0;

      const produit = {
        "nomProd": (values.nomProd || '').trim(),
        "prixProd": Number(values.prixProd) || 0,
        "intituleFam": values.intituleFam || '',
        "idFournisseur": values.fournisseur || '',
        "dateExpiration": values.dateExpiration || '',
        "dateEng": `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
        "qtEntrer": quantiteFinale,
        "speudo": auth.nom || '',
        "codeBarre": values.codeBarre || '',
        "FormFarmaceutik": values.formepharmaceutique || '',
        "tva": Number(values.tva) || 0,
        "boites": Number(values.boites) || 0,
        "prixBoite": Number(values.prixBoite) || 0,
        "pieces": Number(values.pieces) || 0,
        "prix_unitaire": Number(values.prix_unitaire) || 0, // Nouveau champ inclus
      };

      if (isEditMode && productId) {
        // Mode édition - mettre à jour le produit existant
        await produitService.updateProduit(produit, productId);
      } else {
        // Mode création - ajouter un nouveau produit
        await CliniqueService.saveProd(produit);
        toast.success("Produit ajouté avec succès", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
      
      resetForm();
      setTypeInjectable('');
      setFormErrors({});
      
      // Fermer le dialog et rafraîchir si nécessaire
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur:', error);
      const action = isEditMode ? "modification" : "ajout";
      toast.error(`❌ Erreur lors de la ${action} du produit`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setSubmitting(false);
    }
  };

  // Fonction pour gérer le changement de forme pharmaceutique
  const handleFormeChange = (value, setFieldValue) => {
    const forme = value?.FormFarmaceutik || '';
    setFieldValue("formepharmaceutique", forme);
    setTypeInjectable(forme);
    
    // Réinitialiser les champs injectables si on change de forme
    if (forme !== 'Injectables') {
      setFieldValue("boites", 0);
      setFieldValue("prixBoite", 0);
      setFieldValue("pieces", 0);
    }
  };

  // Fonction pour obtenir la valeur de quantité affichée
  const getQuantiteValue = (values) => {
    if (typeInjectable === 'Injectables') {
      return (Number(values.boites) || 0) * (Number(values.pieces) || 0);
    }
    return isEditMode ? (Number(values.quantite) || 0) : 0;
  };

  // Fonction pour gérer le changement de champ avec validation en temps réel
  const handleFieldChange = (fieldName, value, setFieldValue, handleChange) => {
    console.log(`Champ ${fieldName} changé:`, value);
    setFieldValue(fieldName, value);
    
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: undefined
      }));
    }
    
    // Appeler aussi la fonction handleChange de Formik pour la gestion standard
    if (handleChange) {
      handleChange({
        target: {
          name: fieldName,
          value: value
        }
      });
    }
  };

  // Fonction spécifique pour la date
  const handleDateChange = (value, setFieldValue) => {
    console.log('Date sélectionnée:', value);
    handleFieldChange('dateExpiration', value, setFieldValue);
  };

  return (
    <Box>
      {/* En-tête du formulaire */}
      <Box className={classes.formHeader}>
        <BusinessIcon 
          className={isEditMode ? classes.editHeaderIcon : classes.headerIcon} 
        />
        <div className={classes.formTitle}>
          {isEditMode ? 'Modifier le Produit' : 'Nouveau Produit'}
        </div>
        <div className={classes.formSubtitle}>
          {isEditMode 
            ? 'Modifiez les informations du produit existant' 
            : 'Ajoutez un nouveau produit à l\'inventaire'
          }
        </div>
      </Box>

      <Formik 
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={2}>
              
              {/* Informations de base */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <BusinessIcon /> Informations de base
                </div>
              </Grid>

              {/* Nom du produit */}
              <Grid item xs={12} className={classes.gridItem}>
                <TextField 
                  name='nomProd' 
                  label='Nom du produit' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  value={props.values.nomProd}
                  onChange={(e) => handleFieldChange('nomProd', e.target.value, props.setFieldValue, props.handleChange)}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.nomProd)}
                  helperText={formErrors.nomProd}
                  required 
                  InputProps={{
                    startAdornment: <BusinessIcon className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Prix de vente et Prix unitaire */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <TextField 
                  name='prixProd' 
                  label='Prix de vente' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  type="number"
                  value={props.values.prixProd}
                  onChange={(e) => handleFieldChange('prixProd', e.target.value, props.setFieldValue, props.handleChange)}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.prixProd)}
                  helperText={formErrors.prixProd}
                  required 
                  InputProps={{
                    startAdornment: <LocalOfferIcon className={classes.iconAdornment} />,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <TextField 
                  name='prix_unitaire' 
                  label='Prix unitaire' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  type="number"
                  value={props.values.prix_unitaire}
                  onChange={(e) => handleFieldChange('prix_unitaire', e.target.value, props.setFieldValue, props.handleChange)}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.prix_unitaire)}
                  helperText={formErrors.prix_unitaire}
                  InputProps={{
                    startAdornment: <MonetizationOnIcon className={classes.iconAdornment} />,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  placeholder="Optionnel"
                />
              </Grid>

              {/* Famille */}
              <Grid item xs={12} className={classes.gridItem}>
                <Autocomplete
                  id="intituleFam"
                  options={familles}
                  getOptionLabel={option => option?.intituleFam || ''}
                  value={familles.find(f => f.intituleFam === props.values.intituleFam) || null}
                  onChange={(e, v) => {
                    handleFieldChange('intituleFam', v?.intituleFam || '', props.setFieldValue);
                  }}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      label="Famille" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      error={Boolean(formErrors.intituleFam)}
                      helperText={formErrors.intituleFam}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <CategoryIcon className={classes.iconAdornment} />
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Détails techniques */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <Edit /> Détails techniques
                </div>
              </Grid>

              {/* TVA et Forme pharmaceutique */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <TextField 
                  name='tva' 
                  label='TVA (%)' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  type="number"
                  value={props.values.tva}
                  onChange={(e) => handleFieldChange('tva', e.target.value, props.setFieldValue, props.handleChange)}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.tva)}
                  helperText={formErrors.tva}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Autocomplete
                  id="formepharmaceutique"
                  options={formes}
                  getOptionLabel={option => option?.FormFarmaceutik || ''}
                  value={formes.find(f => f.FormFarmaceutik === props.values.formepharmaceutique) || null}
                  onChange={(e, v) => handleFormeChange(v, props.setFieldValue)}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      label="Forme pharmaceutique" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      error={Boolean(formErrors.formepharmaceutique)}
                      helperText={formErrors.formepharmaceutique}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Edit className={classes.iconAdornment} />
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Section Injectables conditionnelle */}
              {typeInjectable === 'Injectables' && (
                <Grid item xs={12}>
                  <Zoom in timeout={500}>
                    <Box className={classes.injectableSection}>
                      <div className={classes.sectionTitle}>
                        📦 Détails Injectables
                      </div>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField 
                            name="boites" 
                            label='Nombre de boites' 
                            fullWidth 
                            variant="outlined"
                            size="small"
                            type="number"
                            value={props.values.boites}
                            onChange={(e) => handleFieldChange('boites', e.target.value, props.setFieldValue, props.handleChange)}
                            onBlur={props.handleBlur}
                            error={Boolean(formErrors.boites)}
                            helperText={formErrors.boites}
                            InputProps={{
                              inputProps: { min: 0 }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField 
                            name="prixBoite" 
                            label='Prix de la boite' 
                            fullWidth 
                            variant="outlined"
                            size="small"
                            type="number"
                            value={props.values.prixBoite}
                            onChange={(e) => handleFieldChange('prixBoite', e.target.value, props.setFieldValue, props.handleChange)}
                            onBlur={props.handleBlur}
                            error={Boolean(formErrors.prixBoite)}
                            helperText={formErrors.prixBoite}
                            InputProps={{
                              inputProps: { min: 0, step: 0.01 }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField 
                            name="pieces" 
                            label='Pièces par boite' 
                            fullWidth 
                            variant="outlined"
                            size="small"
                            type="number"
                            value={props.values.pieces}
                            onChange={(e) => handleFieldChange('pieces', e.target.value, props.setFieldValue, props.handleChange)}
                            onBlur={props.handleBlur}
                            error={Boolean(formErrors.pieces)}
                            helperText={formErrors.pieces}
                            InputProps={{
                              inputProps: { min: 0 }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Zoom>
                </Grid>
              )}

              {/* Informations supplémentaires */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <BusinessIcon /> Informations supplémentaires
                </div>
              </Grid>

              {/* Date expiration et Fournisseur */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <SimpleDatePicker
                  name="dateExpiration"
                  label="Date d'expiration"
                  value={props.values.dateExpiration}
                  onChange={(e) => handleDateChange(e.target.value, props.setFieldValue)}
                  error={Boolean(formErrors.dateExpiration)}
                  helperText={formErrors.dateExpiration}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <Autocomplete
                  id="fournisseur"
                  options={fournisseurs}
                  getOptionLabel={option => option?.nomFournisseur || ''}
                  value={fournisseurs.find(f => f.idFournisseur === props.values.fournisseur) || null}
                  onChange={(e, v) => {
                    handleFieldChange('fournisseur', v?.idFournisseur || '', props.setFieldValue);
                  }}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      label="Fournisseur" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      error={Boolean(formErrors.fournisseur)}
                      helperText={formErrors.fournisseur}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <BusinessIcon className={classes.iconAdornment} />
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Quantité et Code barre */}
              <Grid item xs={12} md={6} className={classes.gridItem}>
                <TextField
                  name="quantite"
                  label='Quantité'
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="number"
                  value={0}         
                  onChange={(e) => {
                    handleFieldChange('quantite', e.target.value, props.setFieldValue, props.handleChange);
                  }}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.quantite)}
                  helperText={formErrors.quantite}
                  required
                  disabled
                  InputProps={{
                    inputProps: { 
                      min: 0
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} className={classes.gridItem}>
                <TextField 
                  name="codeBarre" 
                  label='Code barre' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  value={props.values.codeBarre}
                  onChange={(e) => handleFieldChange('codeBarre', e.target.value, props.setFieldValue, props.handleChange)}
                  onBlur={props.handleBlur}
                  error={Boolean(formErrors.codeBarre)}
                  helperText={formErrors.codeBarre}
                  InputProps={{
                    startAdornment: <BarcodeIcon className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Indication calcul automatique */}
              {typeInjectable === 'Injectables' && (
                <Grid item xs={12}>
                  <Fade in timeout={700}>
                    <Box style={{ 
                      padding: '12px 16px', 
                      backgroundColor: '#f0f9ff', 
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      color: '#0369a1',
                      fontSize: '0.875rem',
                      marginBottom: '16px'
                    }}>
                      <strong>Calcul automatique :</strong> {props.values.boites || 0} boites × {props.values.pieces || 0} pièces = {(props.values.boites || 0) * (props.values.pieces || 0)} unités
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Indication mode édition */}
              {isEditMode && (
                <Grid item xs={12}>
                  <Fade in timeout={700}>
                    <Box style={{ 
                      padding: '12px 16px', 
                      backgroundColor: '#fff3e0', 
                      border: '1px solid #ff9800',
                      borderRadius: '8px',
                      color: '#e65100',
                      fontSize: '0.875rem',
                      marginBottom: '16px'
                    }}>
                      <strong>Mode édition :</strong> La quantité ne peut pas être modifiée en mode édition.
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={isEditMode ? classes.editButton : classes.submitButton}
                  startIcon={isEditMode ? <Save /> : <AddIcon />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting 
                    ? (isEditMode ? 'Modification en cours...' : 'Ajout en cours...') 
                    : (isEditMode ? 'Modifier le produit' : 'Ajouter le produit')
                  }
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default RegistrationForm;